/* eslint-disable func-names */
import React, { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import isContainer from "is-container";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory, useParams } from "react-router-dom";
import * as yup from "yup";

import Breadcrumbs from "../../../../components/Generic/Breadcrump";
import UITabMaterial, { PanelProps } from "../../../../components/Generic/UITabMaterial";
import { PRIVATE_ROUTES } from "../../../../constants";
import {
  ShipmentTrackingProvider,
  useShipmentTrackingContext,
} from "../../../../context/ShipmentTrackingContext";
import { useAppState } from "../../../../context/StateContext";
import useActionShipmentTracking from "../../../../hooks/useActionShipmentTracking";
import useYupValidation from "../../../../hooks/useYupValidation";
import { Shipment } from "../../../../models/shipments";
import { ShipmentsTracking } from "../../../../models/shipments/shipments.tracking";
import { Timezone } from "../../../../models/timezone";
import CarrierService from "../../../../services/carrier.service";
import TimezoneService from "../../../../services/timezone.service";
import { CarrierTdType, CarrierType, Status } from "../../../../util/enums";
import { shipmentStatus } from "../../../../util/shipmentStatus";
import BadgeShipmentStatus from "../components/BadgeShipmentStatus";
import Details, { SkeletonDetails } from "./Details";
import useStyles from "./styles";

const ShipmentTrackingMaintenanceScreen = () => {
  const { t: translate } = useTranslation();
  const { currentUser, hasUnsavedChanges, setHasUnsavedChanges } = useAppState();
  const history = useHistory();
  const classes = useStyles();

  const { shipmentTrackingId } = useParams<{ shipmentTrackingId?: string | undefined }>();

  useEffect(() => {
    if (!shipmentTrackingId) {
      history.replace(PRIVATE_ROUTES.SHIPMENTS_TRACKING);
    }
  }, [history, shipmentTrackingId]);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const [timezones, setTimezones] = useState<Array<Timezone>>([]);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

  const { shipmentTracking, setShipmentTracking } = useShipmentTrackingContext();

  const { data: carrierListResponse } = useQuery(
    ["list.carrier"],
    () =>
      CarrierService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        type: CarrierType.SEA,
        include: JSON.stringify([{ association: "trackableBy" }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    }
  );

  const { getShipmentTrackingById, useCreateShipmentTracking, useUpdateShipmentTracking } =
    useActionShipmentTracking();

  useQuery(
    ["timezones"],
    () =>
      TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) setTimezones(response.data);
      },
    }
  );

  const { isFetching: isFetchingShipmentTracking } = getShipmentTrackingById(
    Number(shipmentTrackingId)
  );

  const { mutate: createShipmentTracking, isLoading: loadingCreateShipmentTracking } =
    useCreateShipmentTracking();

  const { mutate: updateShipmentTracking, isLoading: loadingUpdateShipmentTracking } =
    useUpdateShipmentTracking();

  const schema = yup.object().shape({
    scac: yup.string().notRequired(),
    newObject: yup.object().when("scac", {
      is: (scac: string) => !scac || !scac?.trim().length,
      then: yup
        .object()
        .shape({
          containerNumbers: yup.string().notRequired(),
          bookingNo: yup.string().notRequired(),
          billOfLanding: yup.string().notRequired(),
        })
        .test(
          "is-required",
          translate("thisFieldIsRequired"),
          function ({ containerNumbers, bookingNo, billOfLanding }) {
            if (containerNumbers !== "" || bookingNo !== "" || billOfLanding !== "") {
              if (containerNumbers && isContainer(containerNumbers)) return true;

              if (containerNumbers && !isContainer(containerNumbers))
                return this.createError({
                  path: "newObject.containerNumbers",
                  message: translate("pleaseAddAValidContainerNumber"),
                });

              return true;
            }

            return false;
          }
        ),
      otherwise: yup.object().when("scac", {
        is: (scac: string) => scac !== "AUTO",
        then: yup.object().shape({
          validateContainerNumber: yup.boolean(),
          validatebillOfLanding: yup.boolean(),
          validateBookingNo: yup.boolean(),
          billOfLanding: yup.string().when("validatebillOfLanding", {
            is: true,
            then: yup
              .string()
              .test("is-required", translate("thisFieldIsRequired"), function (value) {
                const { bookingNo, containerNumbers, validateContainerNumber, validateBookingNo } =
                  this.parent;

                if (
                  value !== "" ||
                  (validateBookingNo && bookingNo !== "") ||
                  (validateContainerNumber && containerNumbers !== "")
                )
                  return true;

                return false;
              }),
            otherwise: yup.string().notRequired().nullable(),
          }),
          bookingNo: yup.string().when("validateBookingNo", {
            is: true,
            then: yup
              .string()
              .test("is-required", translate("thisFieldIsRequired"), function (value) {
                const {
                  billOfLanding,
                  containerNumbers,
                  validatebillOfLanding,
                  validateContainerNumber,
                } = this.parent;

                if (
                  value !== "" ||
                  (validatebillOfLanding && billOfLanding !== "") ||
                  (validateContainerNumber && containerNumbers !== "")
                )
                  return true;

                return false;
              }),
            otherwise: yup.string().notRequired().nullable(),
          }),
          containerNumbers: yup.string().when("validateContainerNumber", {
            is: true,
            then: yup
              .string()
              .test("is-container", translate("thisFieldIsRequired"), function (value) {
                const { bookingNo, billOfLanding, validateBookingNo, validatebillOfLanding } =
                  this.parent;

                if (value && value !== "") {
                  if (isContainer(value)) return true;
                  if (!isContainer(value)) {
                    return this.createError({
                      message: translate("pleaseAddAValidContainerNumber"),
                    });
                  }
                } else if ((validateBookingNo && bookingNo !== "") || (validatebillOfLanding && billOfLanding !== "")) return true;

                return false;
              }),
            otherwise: yup.string().notRequired().nullable(),
          }),
        }),
      }),
    }),
  });

  const getValidationFromCarrier = (trackableBy: string, shipment?: ShipmentsTracking) => {
    const carrier = carrierListResponse?.data?.find(
      (record) => record.scac === shipment?.carrierScac
    );
    const isTrackableBy = carrier?.trackableBy?.find((tkb) => tkb.code === trackableBy);

    return !!isTrackableBy;
  };

  const getValuesForComparation = (shipment?: ShipmentsTracking) => {
    const carrier = carrierListResponse?.data?.find((c) => c.scac === shipment?.carrierScac);

    const trackableFields = carrier?.trackableBy?.map((crt) => crt.code);

    return trackableFields;
  };

  const handleValidate = (stUpdate?: ShipmentsTracking): boolean => {
    const st = stUpdate || shipmentTracking;

    try {
      schema.validateSync(
        {
          scac: st?.carrierScac,
          valueToCompareFor: getValuesForComparation(st),
          newObject: {
            validateContainerNumber: getValidationFromCarrier(CarrierTdType.CN, st),
            validatebillOfLanding: getValidationFromCarrier(CarrierTdType.BL, st),
            validateBookingNo: getValidationFromCarrier(CarrierTdType.BK, st),

            containerNumbers: st?.containerNumbers ?? "",
            bookingNo: st?.bookingNo ?? "",
            billOfLanding: st?.billOfLanding ?? "",
          },
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const validateAndSaveShipmentTracking = (shipmentTrackingUpdated: ShipmentsTracking) => {
    setHasBeenSubmitted(true);

    if (!handleValidate(shipmentTrackingUpdated)) return;
    if (!shipmentTrackingUpdated) return;

    const shipmentTrackingToSave: Shipment = { ...shipmentTrackingUpdated };

    // TODO passar para dentro de details
    if (shipmentTrackingToSave.consignee && !shipmentTrackingToSave.consignee.timezoneId) {
      if (timezones && timezones.length > 0) {
        const timezoneFound = timezones.find(
          (timezone) => timezone.name === Intl.DateTimeFormat().resolvedOptions().timeZone
        );

        if (timezoneFound) {
          shipmentTrackingToSave.consignee = {
            ...shipmentTrackingToSave?.consignee,
            timezoneId: timezoneFound?.id,
            companyId: shipmentTrackingToSave?.consignee?.companyId ?? currentUser?.companyId,
          };
        }
      }
    }

    setHasUnsavedChanges(false);

    if (shipmentTrackingToSave.id) {
      updateShipmentTracking({
        id: shipmentTrackingToSave.id,
        shipmentTracking: shipmentTrackingToSave,
      });
    } else {
      createShipmentTracking({
        shipmentTracking: {
          ...shipmentTrackingToSave,
          user: currentUser,
          company: currentUser?.company,
          companyId: currentUser?.companyId,
          projectId: currentUser?.projects[0]?.id,
          eventType: "test",
        },
      });
    }
  };

  const onChangeShipmentTracking = (newShipmentTracking: ShipmentsTracking) => {
    setHasUnsavedChanges(true);
    setShipmentTracking(newShipmentTracking);
  };

  const goBack = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace(PRIVATE_ROUTES.SHIPMENTS_TRACKING);
    }
  };

  const titlePage = () => {
    const renderShipInfo = () => {
      return (
        <div className={classes.shipmentStatus}>
          <BadgeShipmentStatus
            value={shipmentStatus(shipmentTracking?.shipmentTracking?.shipmentStatus)}
          />

          <span className={classes.shipmentStatusTitle} data-cy="title-page-customer">
            {translate("status")}
          </span>
        </div>
      );
    };

    const getShipSubtitle = () => {
      return shipmentTrackingId ? translate("editShipment") : translate("createShipment");
    };

    return (
      <header className={classes.shipmentsHeader}>
        <Breadcrumbs
          items={[
            {
              title: translate("shipment"),
              link: {
                url: PRIVATE_ROUTES.SHIPMENTS_TRACKING,
              },
            },
            {
              title: getShipSubtitle(),
              color: "#465869",
              dataCy: "shipmentsTrackingTitle",
            },
          ]}
        />
        {renderShipInfo()}
      </header>
    );
  };

  const detailsPanel: PanelProps = {
    key: "details",
    menuItem: translate("details"),
    className: classes.uiTab,
    loading: isFetchingShipmentTracking,
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <Details
        shipmentTracking={shipmentTracking}
        updateShipmentTracking={onChangeShipmentTracking}
        fieldHasError={fieldHasError}
        getErrorByField={getErrorByField}
        isUpdatingAction={loadingUpdateShipmentTracking || loadingCreateShipmentTracking}
        saveShipmentTracking={(shipment) => validateAndSaveShipmentTracking(shipment)}
        onCancel={goBack}
      />
    ),
  };

  const leftPanels: PanelProps[] = [detailsPanel];

  useEffect(() => {
    if (hasBeenSubmitted) handleValidate();
  }, [hasBeenSubmitted, shipmentTracking]);

  if (!shipmentTrackingId) {
    return null;
  }

  return (
    <>
      {titlePage()}
      <div className={classes.maintenanceDiv}>
        <div className={classes.shipmentTabs}>
          <Prompt when={hasUnsavedChanges} message="" />
          <UITabMaterial
            leftPanels={leftPanels}
            showRightPanel={false}
            showActivityButton
            leftDataCy="left-shipment-tabs"
            rightDataCy="right-shipment-tabs"
          />
        </div>
      </div>
    </>
  );
};

const ShipmentTrackingMaintenance = () => {
  return (
    <ShipmentTrackingProvider>
      <ShipmentTrackingMaintenanceScreen />
    </ShipmentTrackingProvider>
  );
};

export default ShipmentTrackingMaintenance;
